import type { Middleware } from "@reduxjs/toolkit";
import { isFulfilled, isRejectedWithValue } from "@reduxjs/toolkit";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from "@reduxjs/toolkit/query";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import AttorneyHubAPIService from "_shared/_api/attorney-hub-api-service";
import { attachRtkQueryError, readRtkQueryError } from "_shared/_errors/api-error";
import { reportApiError } from "_shared/_errors/client-error-reporter";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import {
  clearSystemNotificationBanner,
  setSystemNotificationBanner,
} from "_shared/_providers/banner/system-notification-banner-provider";
import { AlertType } from "_shared/_views/system-notification-banner/system-notification-banner";
import Session from "users/__session/session";

export type AttorneyHubApiMeta = FetchBaseQueryMeta & { endpointName: string };

const rawBaseQuery = fetchBaseQuery({
  baseUrl: AttorneyHubAPIService.apiBaseUrl,
  // Resolve fetch when a request is made. Jest's node environment does not
  // expose fetch during module initialization, even though API specs install
  // it before dispatching requests.
  fetchFn: (...args) => globalThis.fetch(...args),
  prepareHeaders: (headers, { arg }) => {
    const authToken = Session.loadFromStorage(() => {}).authToken?.value;
    if (authToken) {
      headers.set("Authorization", `Bearer ${authToken}`);
    }

    // At this point `headers` holds only what the endpoint's own query() declared,
    // and fetchBaseQuery has not yet applied its application/json default. So an
    // absent Content-Type means the endpoint expressed no preference: PATCH bodies
    // are RFC 6902 patch documents unless an endpoint names its own media type.
    if (isPatchArg(arg) && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json-patch+json");
    }
    return headers;
  },
});

function isPatchArg(arg: string | FetchArgs): boolean {
  return typeof arg !== "string" && arg.method?.toUpperCase() === "PATCH";
}

const baseQueryWithEndpointName: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  unknown,
  AttorneyHubApiMeta
> = async (args, queryApi, extraOptions) => {
  const result = await rawBaseQuery(args, queryApi, extraOptions as Record<string, never>);
  if (result.error !== undefined) {
    const error = readRtkQueryError(result.error, { headers: result.meta?.response?.headers });
    reportApiError(error, queryApi.endpoint);
    return {
      error: attachRtkQueryError(result.error, error),
      meta: result.meta ? { ...result.meta, endpointName: queryApi.endpoint } : undefined,
    };
  }

  return {
    ...result,
    meta: result.meta ? { ...result.meta, endpointName: queryApi.endpoint } : undefined,
  };
};

export function getApiEndpointName(meta: unknown): string | undefined {
  if (!meta || typeof meta !== "object" || !("endpointName" in meta)) {
    return undefined;
  }

  const endpointName = (meta as { endpointName: unknown }).endpointName;
  return typeof endpointName === "string" ? endpointName : undefined;
}

/**
 * Every cache tag the one shared api instance knows about.
 *
 * Exported because a test that preloads api state has to name all of them, and hand-listing
 * them there meant that adding a tag for one domain broke an unrelated spec in another. Build
 * that fixture from this list instead.
 */
export const API_TAG_TYPES = [
  "AdministratorInvitation",
  "AssistantConversation",
  "Bookmark",
  "CalendarEvent",
  "ClientProposalTemplate",
  "Company",
  "Document",
  "EntityMember",
  "EntityOfficerInvitation",
  "FeeSchedule",
  "FeeScheduleCategory",
  "FeeScheduleTemplate",
  "Individual",
  "KnowledgeBaseArticle",
  "KnowledgeBaseCategory",
  "KnowledgeBaseLibrary",
  "NetworkedGroup",
  "NetworkInvitation",
  "Proposal",
  "Team",
  "TeamTemplate",
  "UserCoworker",
  "UserInformation",
  "UserEntityMemberInvitation",
  "UserNetworkConnection",
  "VendorRolloutBoard",
  "Marketplace",
  "Forum",
  "ForumInvitation",
  "ForumSubscriber",
  "Message",
  "MessageAttachment",
  "Notification",
  "SystemNotification",
  "Permission",
  "PermissionTemplate",
  "WorkAgreement",
  "OnboardingTemplateCatalog",
  "OnboardingTemplateDetail",
  "TutorialPreferences",
] as const;

export type ApiTagType = (typeof API_TAG_TYPES)[number];

export const api = createApi({
  baseQuery: baseQueryWithEndpointName,
  tagTypes: API_TAG_TYPES,
  onSchemaFailure: logSchemaFailure,
  catchSchemaFailure: () => ({
    status: "CUSTOM_ERROR" as const,
    error: "API response did not match the expected schema.",
  }),

  endpoints: () => ({}),
});

const browserConnectionFailureStatus = "FETCH_ERROR";

function isApiAction(action: unknown): action is { type: string } {
  return (
    typeof action === "object" &&
    action !== null &&
    "type" in action &&
    typeof (action as { type: unknown }).type === "string" &&
    (action as { type: string }).type.startsWith(`${api.reducerPath}/`)
  );
}

function isBrowserConnectionFailure(payload: unknown): payload is { status: typeof browserConnectionFailureStatus } {
  return (
    typeof payload === "object" &&
    payload !== null &&
    "status" in payload &&
    (payload as { status: unknown }).status === browserConnectionFailureStatus
  );
}

export const apiServerDownCheck: Middleware = () => (next) => (action) => {
  const result = next(action);
  if (isFulfilled(action)) {
    clearSystemNotificationBanner();
    return result;
  }
  if (!isApiAction(action) || !isRejectedWithValue(action)) {
    return result;
  }
  if (isAbortedError(action.error)) {
    return result;
  }

  if (isBrowserConnectionFailure(action.payload)) {
    setSystemNotificationBanner(
      AlertType.Error,
      "Attorney Hub is currently unavailable. Please check your internet connection and try again later.",
    );
  }

  return result;
};
function isAbortedError(error: unknown): error is { message: string } {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    (error as { message: string }).message.toLowerCase().includes("aborted")
  );
}
